import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  reviewableDocumentInputSchema,
} from '@bordrio/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CheckedInput, FormLabel } from '@src/components';
import { useUpdateNifOrderMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import ReviewableDocumentFileInput from '../../components/ReviewableDocumentFileInput/ReviewableDocumentFileInput';
import { extractReviewableDocumentInputFromNifOrder } from '../../utils/reviewable-document';
import { NifOrderStepPageProps } from '../types/stepProps';
import { verificationCheckboxSchema } from '../utils/validation';

const formSchema = z.object({
  birthCertificate: reviewableDocumentInputSchema,
  verifications: z.object({
    nameVisible: verificationCheckboxSchema,
    imageClear: verificationCheckboxSchema,
    noShadows: verificationCheckboxSchema,
  }),
});

type FormData = z.infer<typeof formSchema>;

function NifPortugueseBirthCertificatePage({
  nifOrder,
  previousUrl,
  navigateToNextStep,
}: NifOrderStepPageProps): JSX.Element {
  const birthCertificateDoc = extractReviewableDocumentInputFromNifOrder(
    nifOrder,
    'nif_portuguese_birth_certificate',
    'portuguese_birth_certificate',
  );

  const hasDocument = !!birthCertificateDoc.currentVersion?.fileOne;

  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      birthCertificate: birthCertificateDoc,
      verifications: {
        nameVisible: hasDocument || undefined,
        imageClear: hasDocument || undefined,
        noShadows: hasDocument || undefined,
      },
    },
    resolver: zodResolver(formSchema),
  });

  const { t } = useTranslation(['nif']);
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const toast = useToast();

  function onSubmit(formData: FormData): Promise<void> {
    return updateNifOrder({
      variables: {
        input: {
          id: nifOrder.id,
          data: {
            intakeDocuments: [
              {
                documentCategoryId:
                  REVIEWABLE_DOCUMENT_CATEGORIES
                    .nif_portuguese_birth_certificate.id,
                reviewableDocument: formData.birthCertificate,
              },
            ],
          },
        },
      },
    })
      .then((updatedOrder) => {
        navigateToNextStep(updatedOrder?.data?.updateNifOrder?.nifOrder);
      })
      .catch((err) => toast.error(logAndFormatError(err)));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>
          {t(
            'portuguese_birth_certificate.title',
            'Portuguese birth certificate',
          )}
        </h2>
        <p>
          <Trans
            t={t}
            i18nKey="portuguese_birth_certificate.body"
            defaults="Provide a copy of the applicant's Portugal-issued birth certificate. If they do not have one, you can order one <a>here</a>."
            components={{
              a: (
                // we escape this content to pass it through the string;
                // it definitely *has* content
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  className="font-medium"
                  href="https://www.bordr.com/birth-certificate"
                  aria-label={t(
                    'portuguese_birth_certificate.birth_certs_sr',
                    'Birth certificates at Bordr',
                  )}
                />
              ),
            }}
          />
        </p>
      </div>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <ReviewableDocumentFileInput
          control={control}
          name="birthCertificate"
        />
        <div />
      </div>
      <div className="space-y-4">
        <FormLabel>
          {t(
            'upload_checklists.instructions',
            'Please verify the following regarding your upload:',
          )}
        </FormLabel>
        <CheckedInput.LabelledController
          control={control}
          name="verifications.nameVisible"
          label={t(
            'portuguese_birth_certificate.checklist.birth_cert_minor_parents_names',
            'Full names of both parents are visible (unless represented by a single parent)',
          )}
        />
        <CheckedInput.LabelledController
          control={control}
          name="verifications.imageClear"
          label={t(
            'portuguese_birth_certificate.checklist.birth_cert_clear_image',
            'Image is clear',
          )}
        />
        <CheckedInput.LabelledController
          control={control}
          name="verifications.noShadows"
          label={t(
            'portuguese_birth_certificate.checklist.no_shadows_reflections',
            'No shadows or light reflections',
          )}
        />
      </div>
      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default NifPortugueseBirthCertificatePage;
